import {
  query,
  mutation,
  chimeraQuery,
  chimeraMutation,
} from '@/dex-shared/helpers/graphql';

export default {
  state: {
    admins: [],
    searchedUsers: [],
    schoolManagers: [],
    userToBeManager: null,
    schoolFoundButNoManager: false,
    schoolCourses: [],
  },
  mutations: {
    SCHOOL_FOUND_BUT_NO_MANAGERS(state: any, isTrue) {
      state.schoolFoundButNoManager = isTrue;
    },
    SET_ADMINS(state: any, admins: any) {
      state.admins = admins;
    },

    ADD_ADMIN(state: any, admin: any) {
      state.admins.push(admin);
    },

    PULL_ALL_SCHOOL_COURSES(state: any, schoolCourses: any) {
      state.schoolCourses = schoolCourses;
    },

    REMOVE_ADMIN(state: any, index: number) {
      state.admins.splice(index, 1);
    },

    SET_SEARCHED_USERS(state: any, searchedUsers: any) {
      state.searchedUsers = searchedUsers;
    },
    SET_SCHOOL_MANAGERS(state: any, schoolManagers: any) {
      state.schoolManagers = schoolManagers;
    },
    SET_USER_TO_BE_MANAGER(state: any, user: any) {
      state.userToBeManager = user;
    },
  },
  actions: {
    async PULL_ADMINS({ commit }: any) {
      let response = await query(`query allAdmins {
        allUsers(condition: {role: "admin"}){
          nodes{
            id
            name
            email
            role
          }
        }
      }`);

      let adminNodes = response.data.allUsers;
      if (adminNodes) {
        commit('SET_ADMINS', adminNodes.nodes);
      }
    },
    async PULL_SCHOOL_COURSES({ commit }: any) {
      let response = await chimeraQuery(
        `
      query PullAllSchoolCourses {  
        chimera {
          courses {
            slug
            _id
            name(original: true)
            category{
              name
              id
            }    
          }
        }
      }`,
      );

      let schoolCourse = response.data.chimera.courses;

      if (schoolCourse) {
        commit('PULL_ALL_SCHOOL_COURSES', schoolCourse);
      } else {
        return 'Something went wrong when rendering courses!';
      }
    },
    async DELETE_DEFAULT_WEEKPLANNING_FROM_COURSE(
      { commit }: any,
      courseId: string,
    ) {
      try {
        await chimeraMutation(
          `mutation deleteAllWeekPlanningsFromCourse($courseId: String!) {
            chimera {
              deleteAllWeekPlanningsFromCourse(courseId: $courseId) {
                serial
              }
            }
          }
          `,
          {
            courseId,
          },
        );
        return true;
      } catch (err) {
        return err;
      }
    },
    async CLONE_ALL_WEEKPLANNINGS_FROM_COURSE(
      { commit }: any,
      { courseFromId, courseToId },
    ) {
      try {
        await chimeraMutation(
          `mutation cloneAllWeekPlanningFromCourse($courseFromId: String!, $courseToId: String!) {
            chimera {
              cloneWeekPlanningsFromCourse(
                courseFromId: $courseFromId, 
                courseToId: $courseToId
              ) {
                serial
              }
            }
          }
          `,
          {
            courseFromId,
            courseToId,
          },
        );
        return true;
      } catch (err) {
        return console.log(err);
      }
    },
    async PULL_SCHOOL_MANAGERS({ commit, rootState }: any, schoolId: number) {
      let response = await query(
        `query schoolManager($schoolId: Int!) {
        classroomById(id: $schoolId) {
          name
          staffByClassroomId {
            nodes {
              id
              userByUserId {
                id
                name
                email
              }
            }
          }
        }
      }`,
        { schoolId },
      );
      let schoolManagersNodes = response.data.classroomById.staffByClassroomId.nodes.map(
        node =>
          Object.assign(node.userByUserId, {
            schoolName: response.data.classroomById.name,
            staffId: node.id,
          }),
      );
      if (response.data.classroomById.name) {
        commit('SCHOOL_FOUND_BUT_NO_MANAGERS', true);
      }
      if (schoolManagersNodes) {
        commit('SET_SCHOOL_MANAGERS', schoolManagersNodes);
      }
    },
    async PULL_USER_TO_BE_MANAGER({ commit }: any, userEmail: string) {
      let response = await query(
        `query schoolManager($userEmail: String!) {
          userByEmail(email: $userEmail) {
            id
            name
            email
            staffByUserId {
              nodes {
                classroomByClassroomId {
                  name
                }
              }
            }
          }
      }`,
        { userEmail },
      );
      let user = response.data.userByEmail;
      if (user.staffByUserId) {
        user.staffs = user.staffByUserId.nodes.map(
          staff => staff.classroomByClassroomId.name,
        );
      }
      if (user) {
        commit('SET_USER_TO_BE_MANAGER', user);
      }
    },
    async SEARCH_USER({ commit }: any, userEmail: string) {
      let response = await query(
        `query getAllUsersDexAdmin($userEmail: String!) {
        allUsers(filter: {
          email: {
            includesInsensitive: $userEmail
          }
        }) {
          nodes {
            id
            name
            email
            role
          }
        }
      }`,
        {
          userEmail,
        },
      );

      let searchedUsersNodes = response.data.allUsers;
      if (searchedUsersNodes) {
        commit('SET_SEARCHED_USERS', searchedUsersNodes.nodes);
      }
    },

    async ADD_ADMIN({ commit }: any, userId: number) {
      try {
        await mutation(
          `mutation updateUser($userId: Int!) {
            updateUserRole(userId: $userId, role: admin)
          }`,
          {
            userId,
          },
        );
        return true;
      } catch (err) {
        return err;
      }
    },

    async PUSH_NEW_MANAGER(
      { commit, rootState }: any,
      { manager, classroomId },
    ) {
      const userId = manager.id;
      try {
        await mutation(
          `mutation updateUser($classroomId: Int!, $userId: Int!) {
            createStaff(input: {staff: {
              roleId: 1
              classroomId: $classroomId,
              userId: $userId
           }}) {
             staff {
               id
             }
           }
          }`,
          {
            userId,
            classroomId,
          },
        );
        commit('SET_USER_TO_BE_MANAGER', null);
        commit('SET_SCHOOL_MANAGERS', [
          ...rootState.users.schoolManagers,
          manager,
        ]);
        return true;
      } catch (err) {
        return err;
      }
    },

    async REMOVE_MANAGER({ commit, rootState }: any, staffId: number) {
      try {
        await mutation(
          `mutation updateUser($staffId: Int!) {
            deleteStaffById(input: {  id: $staffId }) {
              staff {
                id
              }
            }
          }`,
          {
            staffId,
          },
        );
        commit(
          'SET_SCHOOL_MANAGERS',
          rootState.users.schoolManagers.filter(
            manager => manager.staffId !== staffId,
          ),
        );
        return true;
      } catch (err) {
        return err;
      }
    },

    async REMOVE_ADMIN({ commit }: any, userId: number) {
      try {
        await mutation(
          `mutation updateUser($userId: Int!) {
            updateUserRole(userId: $userId, role: user)
          }`,
          {
            userId,
          },
        );
        return true;
      } catch (err) {
        return err;
      }
    },
  },
};
